import { useLazyQuery } from "@apollo/client/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ExportBatchChangeRequestsJobDryRunResultDocument,
  type ExportBatchChangeRequestsJobDryRunResultQuery,
  type ExportBatchChangeRequestsJobDryRunResultQueryVariables,
} from "~/features/dataImportProjects/gqls";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";
import { downloadFile } from "~/utils/download";

type UseExportBatchChangeRequestsJobDryRunResultProps = {
  jobRunId: string;
  onClose?: () => void;
};

export const useExportBatchChangeRequestsJobDryRunResult = ({
  jobRunId,
  onClose,
}: UseExportBatchChangeRequestsJobDryRunResultProps) => {
  const { t } = useTranslation();
  const [exporting, setExporting] = useState(false);
  const [exportDryRunResult] = useLazyQuery<
    ExportBatchChangeRequestsJobDryRunResultQuery,
    ExportBatchChangeRequestsJobDryRunResultQueryVariables
  >(ExportBatchChangeRequestsJobDryRunResultDocument, {
    fetchPolicy: "no-cache",
  });
  const { tenantId } = useTenant();
  const onExport = () => {
    if (!tenantId) {
      toast.error(translateError(t, new Error("Tenant is not resolved")));
      return;
    }

    setExporting(true);

    exportDryRunResult({
      variables: { input: { tenantId, jobRunId } },
    })
      .then(({ data }) => {
        const url = data?.cnutExportBatchChangeRequestsJobDryRunResult;
        if (!url) {
          toast.error(t("feature.common.failedToExport"));
          return;
        }

        downloadFile(url, `dewey-batch-change-requests-job-dry-run-result-${jobRunId}.csv`);
      })
      .catch((e: unknown) => {
        if (e instanceof Error) {
          toast.error(translateError(t, e));
        }
      })
      .finally(() => {
        setExporting(false);
        onClose?.();
      });
  };

  return {
    onExport,
    exporting,
  };
};
